'use client';

import { Box, HStack, VStack, token } from '@terra/ui-styled-system';
import { Badge } from '../../components/badge';
import {
  BodyDefault,
  HeadingItem,
  MetaDefault,
} from '../../components/typography';
import { Button } from '../../components/button';
import { Card } from '../../components/card';
import { IconButton } from '../../components/icon-button';
import { Switch } from '../../components/switch';
import {
  ArrowDown01Icon,
  ArrowUp01Icon,
  Edit03Icon,
  Delete01Icon,
} from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import type {
  GuardrailAction,
  GuardrailCategory,
  GuardrailPipeline,
} from '@terra/shared-contracts-applications';
import { GuardrailPipelineSchema } from '@terra/shared-schemas-orchestrator';

const guardrailActionLabelKey: Record<
  GuardrailAction,
  'actions.block' | 'actions.log' | 'actions.warn'
> = {
  BLOCK: 'actions.block',
  LOG: 'actions.log',
  WARN: 'actions.warn',
};

export type GuardrailCardGuardrail = {
  id: string;
  name: string;
  description: string | null;
  category: GuardrailCategory;
  enabled: boolean;
  rules: {
    id: string;
    rule: string;
  }[];
  action: GuardrailAction;
  pipelines?: GuardrailPipeline[];
  isDefault: boolean;
  triggerCount: number;
};
export type GuardrailCardProps = {
  guardrail: GuardrailCardGuardrail;
  isExpanded: boolean;
  onToggleExpand: () => void;
  onToggleEnabled: (enabled: boolean) => void;
  onEdit: () => void;
  onDelete?: () => void;
  categoryBadgeLabel?: string;
  readOnly?: boolean;
};

export const GuardrailCard = ({
  guardrail,
  isExpanded,
  onToggleExpand,
  onToggleEnabled,
  onEdit,
  onDelete,
  categoryBadgeLabel,
  readOnly,
}: GuardrailCardProps) => {
  const tGuardrails = useTranslations('designSystem.guardrails');
  const isDisabled = !guardrail.enabled;
  const hasRules = guardrail.rules.length > 0;
  const showExpandedRules = isExpanded && hasRules;
  const resolvedCategoryBadgeLabel =
    categoryBadgeLabel ?? tGuardrails(`categories.${guardrail.category}`);
  const pipelines = guardrail.pipelines ?? [];
  const visiblePipelines =
    pipelines.length === 0 ||
    pipelines.length === GuardrailPipelineSchema.options.length
      ? GuardrailPipelineSchema.options
      : pipelines;
  const pipelineSummary = visiblePipelines
    .map((pipeline) => tGuardrails(`pipelines.${pipeline}`))
    .join(', ');

  return (
    <Card.Root
      w="full"
      bg="bg.neutral.default"
      boxShadow={isDisabled ? 'none' : 'xs'}
      borderWidth="1px"
      borderColor="border.neutral.subtle"
    >
      <Box px={4} py={3}>
        <VStack alignItems="stretch" gap={4}>
          <HStack gap={2} alignItems="flex-start">
            <VStack alignItems="stretch" gap={1.5} flex={1} minW={0}>
              <HStack gap={1} alignItems="center" flexWrap="wrap">
                <HeadingItem color="fg.neutral.default">
                  {guardrail.name}
                </HeadingItem>
                {guardrail.isDefault && (
                  <Badge
                    variant="fill"
                    size="sm"
                    style={{ backgroundColor: token('colors.bg.accent.light') }}
                  >
                    {tGuardrails('card.defaultBadge')}
                  </Badge>
                )}
                <Badge variant="fill" size="sm">
                  {resolvedCategoryBadgeLabel}
                </Badge>
              </HStack>
              <MetaDefault color="fg.neutral.muted">
                {guardrail.description}
              </MetaDefault>
              <MetaDefault color="fg.neutral.subtle">
                {pipelineSummary}
              </MetaDefault>
            </VStack>

            <HStack gap={2} flexShrink={0}>
              <Switch
                size="sm"
                checked={guardrail.enabled}
                disabled={readOnly}
                onCheckedChange={(details) => onToggleEnabled(details.checked)}
              />
              {!readOnly && (
                <>
                  <IconButton
                    size="xs"
                    variant="ghost"
                    onClick={onEdit}
                    aria-label={tGuardrails('editGuardrail')}
                  >
                    <Edit03Icon size={16} />
                  </IconButton>
                  {!guardrail.isDefault && onDelete && (
                    <IconButton
                      size="xs"
                      variant="ghost"
                      onClick={onDelete}
                      aria-label={tGuardrails('deleteGuardrail')}
                    >
                      <Delete01Icon size={16} />
                    </IconButton>
                  )}
                </>
              )}
            </HStack>
          </HStack>

          {showExpandedRules && (
            <VStack alignItems="stretch" gap={2.5}>
              <Box bg="bg.subtle" borderRadius="sm" p={2}>
                <VStack alignItems="stretch" gap={1.5}>
                  {guardrail.rules.map((rule, index) => (
                    <BodyDefault
                      key={index}
                      color="fg.neutral.subtle"
                      overflow="hidden"
                      textOverflow="ellipsis"
                    >
                      {rule.rule}
                    </BodyDefault>
                  ))}
                </VStack>
              </Box>
              <HStack gap={2}>
                <BodyDefault color="fg.neutral.muted">
                  {tGuardrails('card.action')}
                </BodyDefault>
                <BodyDefault color="fg.neutral.default">
                  {tGuardrails(guardrailActionLabelKey[guardrail.action])}
                </BodyDefault>
              </HStack>
            </VStack>
          )}

          <Button
            size="sm"
            variant="ghost"
            onClick={onToggleExpand}
            disabled={!hasRules}
            justifyContent="flex-start"
            alignSelf="flex-start"
            fontWeight="medium"
          >
            {showExpandedRules
              ? tGuardrails('card.hideRules')
              : tGuardrails('card.showRules')}
            {showExpandedRules ? (
              <ArrowUp01Icon size={16} />
            ) : (
              <ArrowDown01Icon size={16} />
            )}
          </Button>
        </VStack>
      </Box>
    </Card.Root>
  );
};
